import jsQR from 'jsqr';

/** Long side of the uploaded photo: plenty for small print on a card, and quick to send over mobile data. */
const MAX_SIDE = 1600;
/** QR codes are found on a smaller copy; a code fills enough of a card photo to survive the shrink. */
const QR_SIDE = 1000;

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file is not a photo the browser can open'));
    };
    img.src = url;
  });
}

function draw(img: HTMLImageElement, maxSide: number) {
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return { canvas, ctx };
}

/**
 * Prepares a card photo on the phone: shrunk to a JPEG of at most MAX_SIDE (browsers already turn photos upright),
 * plus the text of a QR code if the card has one.
 */
export async function prepareCardPhoto(file: Blob): Promise<{ photo: Blob; qrText: string | null }> {
  const img = await loadImage(file);
  const { canvas } = draw(img, MAX_SIDE);
  const photo = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not prepare the photo'))), 'image/jpeg', 0.85));
  let qrText: string | null = null;
  try {
    const small = draw(img, QR_SIDE);
    const data = small.ctx.getImageData(0, 0, small.canvas.width, small.canvas.height);
    qrText = jsQR(data.data, data.width, data.height, { inversionAttempts: 'attemptBoth' })?.data || null;
  } catch {
    // no code, or the browser would not read the pixels: the card is read from the photo alone
  }
  return { photo, qrText };
}
